/** @jsxImportSource ./simboluri-jsx */
import { useEffect, useRef, useState } from "react";
import { useT, useTx } from "../i18n";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { FemModel } from "./tipuri";
import { incadreaza } from "./incadrare3d";
import { axaGrinzii } from "./axaGrinzii";
import {
  hashElement,
  pasElement,
  pasScurt,
  titluPas,
} from "./legaturi";

// „Unde sunt?" — clădirea întreagă, în mic, lângă desenul de detaliu al
// elementului. Nota de calcul a unei grinzi sau a unui
// stâlp arată elementul scos din context: nu se vede dacă e la parter sau la
// ultimul etaj, pe fațadă sau în mijlocul plăcii. Aici e replica modelului
// Pasului 7 — grinzi albastre, stâlpi roșii — cu elementul curent aprins în
// GALBEN.
//
// Se poate și NAVIGA din el: click pe un element te duce la
// pasul care îl armează, cu elementul selectat . Motivul e drumul
// firesc de citire — te uiți la o grindă, vezi pe ce stâlp descarcă și vrei
// stâlpul acela, nu să-l cauți într-un dropdown de 60 de poziții. Regulile:
//
//   * elementul din pagina CURENTĂ nu navighează nicăieri, doar se selectează
//     pe loc (`peElement`) — o navigare la propria pagină ar reîncărca-o
//     degeaba și ar pierde unghiul camerei;
//   * URL-ul se construiește DOAR prin `legaturi.hashElement`, cu id-ul
//     PROIECTULUI (nu al spațiului) și cu soluția și corpul păstrate;
//   * fără `projectId` viewerul rămâne exact ce era: view-only.

// aceeași paletă ca scena principală (v. `CULORI` din StepFem.tsx): două
// vederi 3D ale aceleiași clădiri nu pot avea două limbaje de culoare
const CULORI = {
  stalp: 0xb5323d,
  grinda: 0x3d7cab,
  // pereții de beton ai subsolului — aceeași familie ca în Pasul 7
  perete: 0x459c8b,
  evidentiat: 0xc99b3f,
  placa: 0xcdd8d9,
  panou: 0xa7b4b6,
};

export default function LocalizareCladire({
  model,
  evidentiate,
  height = 380,
  projectId,
  solutie,
  pasCurent,
  peElement,
  cuPanouri = false,
}: {
  model: FemModel | null | undefined;
  // id_plan-urile aprinse în galben (o grindă; la stâlpi, tot stack-ul)
  evidentiate: string[];
  height?: number;
  // navigarea la click — fără ele viewerul rămâne view-only
  projectId?: string;
  solutie?: string | null;
  // cheia pasului în care e afișat viewerul: elementele LUI se selectează pe
  // loc, restul deschid pasul care le armează
  pasCurent?: string;
  peElement?: (ref: string) => void;
  // OCHIURILE de placă, desenate ca suprafețe (nu doar conturul nivelului):
  // le cere Pasul 16, unde elementul paginii E un panou de placă. În
  // celelalte pagini rămân stinse — o suprafață plină ar ascunde barele, iar
  // un click pe ea ar fura selecția grinzii de dedesubt.
  cuPanouri?: boolean;
}) {
  const mount = useRef<HTMLDivElement>(null);
  // ce e sub cursor, ca să se vadă ÎNAINTE de click unde duce clickul
  const [sub, setSub] = useState<{ ref: string; pas: string | null } | null>(
    null
  );
  // handler-ul de click trăiește în scena three.js (efectul de mai jos), dar
  // depinde de props care se schimbă des; ref-ul îl ține mereu proaspăt fără
  // să reconstruiască scena
  const nav = useRef({ projectId, solutie, pasCurent, peElement });
  nav.current = { projectId, solutie, pasCurent, peElement };
  // mesh-urile pe id_plan + funcția care re-pictează, ca schimbarea
  // elementului din dropdown să NU reconstruiască scena (altfel camera sare
  // înapoi în poziția inițială de fiecare dată)
  const pictor = useRef<((ids: string[]) => void) | null>(null);

  useEffect(() => {
    const el = mount.current;
    if (!el || !model) return;
    const W = el.clientWidth || 320;
    const H = height;

    const nd = new Map(model.noduri.map((n) => [n.id, n]));
    const xs = model.noduri.map((n) => n.x_m);
    const ys = model.noduri.map((n) => n.y_m);
    const zs = model.noduri.map((n) => n.z_m);
    const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
    const cy = (Math.min(...ys) + Math.max(...ys)) / 2;
    const cz = (Math.min(...zs) + Math.max(...zs)) / 2;
    const span = Math.max(
      Math.max(...xs) - Math.min(...xs),
      Math.max(...ys) - Math.min(...ys),
      Math.max(...zs) - Math.min(...zs),
      1
    );

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xe8eeee);
    scene.add(new THREE.AmbientLight(0xffffff, 0.8));
    const lumina = new THREE.DirectionalLight(0xffffff, 1.0);
    lumina.position.set(1.5 * span, 0.8 * span, 2 * span);
    scene.add(lumina);

    const camera = new THREE.PerspectiveCamera(42, W / H, 0.05, span * 20);
    camera.up.set(0, 0, 1);
    // direcția de privire; distanța reală o pune `incadreaza`, după ce scena
    // e construită (span-ul nodurilor subestima clădirea în coloana îngustă)
    camera.position.set(cx + span * 0.95, cy - span * 1.15, cz + span * 0.85);

    // preserveDrawingBuffer: canvas-ul rămâne citibil după randare (captură
    // de ecran / export), ca la modelul Pasului 7
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      preserveDrawingBuffer: true,
    });
    renderer.setSize(W, H);
    renderer.setPixelRatio(window.devicePixelRatio || 1);
    el.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.set(cx, cy, cz);
    controls.update();
    const render = () => renderer.render(scene, camera);
    controls.addEventListener("change", render);

    // materialele: cele normale se împart între toate mesh-urile, cel galben
    // are emissive ca elementul aprins să se vadă și în umbră
    const matStalp = new THREE.MeshLambertMaterial({ color: CULORI.stalp });
    const matGrinda = new THREE.MeshLambertMaterial({ color: CULORI.grinda });
    const matPerete = new THREE.MeshLambertMaterial({ color: CULORI.perete });
    const matSel = new THREE.MeshLambertMaterial({
      color: CULORI.evidentiat,
      emissive: 0x554400,
    });
    const matContur = new THREE.LineBasicMaterial({
      color: CULORI.placa,
      transparent: true,
      opacity: 0.75,
    });

    // id_plan -> mesh-urile lui (un stâlp de stack are unul per nivel)
    const dupaId = new Map<string, { mesh: THREE.Mesh; baza: THREE.Material }[]>();
    // mesh-urile care pot fi ATINSE cu raycasterul (barele, nu conturul
    // plăcilor: o linie de contur nu e un element armat)
    const tinte: THREE.Mesh[] = [];
    const tine = (id: string | null | undefined, mesh: THREE.Mesh,
                  baza: THREE.Material) => {
      if (!id) return;
      const lista = dupaId.get(id) ?? [];
      lista.push({ mesh, baza });
      dupaId.set(id, lista);
      mesh.userData.ref = id;
      tinte.push(mesh);
    };

    // aceleași convenții geometrice ca la Pasul 7 (StepFem): stâlpul e rotit
    // pe versorul `dir`, iar grinda merge pe AXA REALĂ (nod + offsetul din
    // zona rigidă), nu până în nodul stâlpului
    model.stalpi.forEach((s) => {
      const a = nd.get(s.nod_jos);
      const b = nd.get(s.nod_sus);
      if (!a || !b) return;
      const L = b.z_m - a.z_m;
      const mesh = new THREE.Mesh(new THREE.BoxGeometry(s.h_m, s.b_m, L), matStalp);
      mesh.position.set(a.x_m, a.y_m, a.z_m + L / 2);
      if (s.dir) mesh.rotation.z = Math.atan2(s.dir[1], s.dir[0]);
      scene.add(mesh);
      tine(s.id_plan, mesh, matStalp);
    });

    model.grinzi.forEach((g) => {
      const a = nd.get(g.nod_i);
      const b = nd.get(g.nod_j);
      if (!a || !b) return;
      const axa = axaGrinzii(g, a, b);
      const [ax, ay] = axa.a;
      const [bx, by] = axa.b;
      const { L } = axa;
      if (L < 1e-6) return;
      const mesh = new THREE.Mesh(new THREE.BoxGeometry(L, g.bw_m, g.h_m), matGrinda);
      mesh.position.set((ax + bx) / 2, (ay + by) / 2,
                        a.z_m - g.h_m / 2 + g.hf_m / 2);
      mesh.rotation.z = axa.unghi;
      scene.add(mesh);
      tine(g.id_plan, mesh, matGrinda);
    });

    // pereții de beton ai subsolului (aceeași geometrie ca în Pasul 7:
    // colț de stâlp → colț de stâlp, coborâți sub placă)
    (model.pereti_beton ?? []).forEach((p) => {
      const [ax0, ay0] = p.a_xy;
      const [bx0, by0] = p.b_xy;
      const dx = bx0 - ax0, dy = by0 - ay0;
      const L = Math.hypot(dx, dy);
      if (L < 1e-6) return;
      const mesh = new THREE.Mesh(
        new THREE.BoxGeometry(L, p.t_m, p.h_web_m), matPerete);
      mesh.position.set(
        (ax0 + bx0) / 2,
        (ay0 + by0) / 2,
        p.z_sus_m - (p.e_offset_m ?? p.hf_m / 2 + p.h_web_m / 2)
      );
      mesh.rotation.z = Math.atan2(dy, dx);
      scene.add(mesh);
      tine(p.id_plan, mesh, matPerete);
    });

    // conturul plăcilor: fără el, un schelet de bare e greu de citit ca
    // clădire — dar rămâne linie subțire, nu suprafață, ca să nu ascundă
    // elementul aprins
    const conturi: THREE.BufferGeometry[] = [];
    // materialele proprii ale panourilor (unul per ochi, ca aprinderea să
    // nu-l schimbe și pe vecin): se eliberează la demontare
    const materiale: THREE.Material[] = [];
    (model.etaje ?? []).forEach((et) => {
      (et.slab ?? []).forEach((rings) => {
        rings.forEach((ring) => {
          const pts = ring.map(([x, y]) => new THREE.Vector3(x, y, et.z_m + 0.02));
          if (pts.length > 1) pts.push(pts[0].clone());
          const geo = new THREE.BufferGeometry().setFromPoints(pts);
          conturi.push(geo);
          scene.add(new THREE.Line(geo, matContur));
        });
      });
    });

    // OCHIURILE de placă, ca suprafețe: în Pasul 16 elementul paginii e un
    // panou, deci trebuie să se poată aprinde și atinge. Poligonul poate avea
    // goluri (ochi inelar) și se desenează chiar la cota plăcii.
    if (cuPanouri) {
      const zNivel = new Map<number, number>();
      (model.etaje ?? []).forEach((et) => zNivel.set(et.nivel_diafragma, et.z_m));
      (model.panouri ?? []).forEach((p) => {
        if ((p.contur ?? []).length < 3) return;
        const forma = new THREE.Shape(
          p.contur.map(([x, y]) => new THREE.Vector2(x, y))
        );
        (p.inele ?? []).forEach((inel) => {
          if ((inel.contur ?? []).length < 3) return;
          forma.holes.push(
            new THREE.Path(inel.contur.map(([x, y]) => new THREE.Vector2(x, y)))
          );
        });
        const geo = new THREE.ShapeGeometry(forma);
        conturi.push(geo);
        const mat = new THREE.MeshLambertMaterial({
          color: CULORI.panou,
          transparent: true,
          opacity: 0.35,
          side: THREE.DoubleSide,
        });
        materiale.push(mat);
        const mesh = new THREE.Mesh(geo, mat);
        mesh.position.z = (zNivel.get(p.nivel) ?? 0) - 0.01;
        scene.add(mesh);
        tine(p.id, mesh, mat);
      });
    }

    // repictarea: doar schimbă materialul, deci unghiul ales de utilizator
    // supraviețuiește trecerii la alt element
    let aprinse: THREE.Mesh[] = [];
    pictor.current = (ids: string[]) => {
      aprinse.forEach((m) => {
        m.material = (m.userData.baza as THREE.Material) ?? matGrinda;
      });
      aprinse = [];
      ids.forEach((id) => {
        (dupaId.get(id) ?? []).forEach(({ mesh, baza }) => {
          mesh.userData.baza = baza;
          mesh.material = matSel;
          aprinse.push(mesh);
        });
      });
      render();
    };

    // --- hover + click ---------------------------------------------------
    // Un click care se termină după o rotire NU e o selecție: OrbitControls
    // consumă tot dragul, deci se compară poziția de la apăsare cu cea de la
    // ridicare și peste 4 px se consideră rotire.
    const ray = new THREE.Raycaster();
    const ptr = new THREE.Vector2();
    const canvas = renderer.domElement;

    const subCursor = (ev: PointerEvent | MouseEvent): string | null => {
      const r = canvas.getBoundingClientRect();
      ptr.x = ((ev.clientX - r.left) / r.width) * 2 - 1;
      ptr.y = -((ev.clientY - r.top) / r.height) * 2 + 1;
      ray.setFromCamera(ptr, camera);
      const hit = ray.intersectObjects(tinte, false)[0];
      return (hit?.object.userData.ref as string) ?? null;
    };

    // starea de hover se rescrie DOAR când se schimbă elementul de sub cursor:
    // un `setSub` la fiecare pixel ar re-randa pagina (nota de calcul e mare)
    // de zeci de ori pe secundă, degeaba
    let ultim: string | null = null;
    const peMove = (ev: PointerEvent) => {
      if (nav.current.projectId == null) return;
      const ref = subCursor(ev);
      const pas = ref ? pasElement(ref) : null;
      canvas.style.cursor = ref && (pas || nav.current.peElement) ? "pointer" : "";
      if (ref === ultim) return;
      ultim = ref;
      setSub(ref ? { ref, pas } : null);
    };

    let apasat: { x: number; y: number } | null = null;
    const peDown = (ev: PointerEvent) => {
      apasat = { x: ev.clientX, y: ev.clientY };
    };
    const peUp = (ev: PointerEvent) => {
      const start = apasat;
      apasat = null;
      if (!start) return;
      if (Math.hypot(ev.clientX - start.x, ev.clientY - start.y) > 4) return;
      const { projectId, solutie, pasCurent, peElement } = nav.current;
      if (projectId == null) return;
      const ref = subCursor(ev);
      if (!ref) return;
      const pas = pasElement(ref);
      // elementul paginii curente: se selectează pe loc, fără navigare
      if (pas && pas === pasCurent) {
        peElement?.(ref);
        return;
      }
      if (!pas) return;
      // Click pe alt element ⇒ PAGINA ELEMENTULUI, nu pagina pasului care îl
      // armează: pagina de element e vederea de intrare
      // — verificările lui într-un tabel simplu, 3D-ul și contextul —, iar de
      // acolo se intră în pasul cu detaliile complete.
      window.location.href = hashElement(projectId, ref, solutie ?? null);
    };

    canvas.addEventListener("pointermove", peMove);
    canvas.addEventListener("pointerdown", peDown);
    canvas.addEventListener("pointerup", peUp);
    const peLeave = () => {
      ultim = null;
      setSub(null);
    };
    canvas.addEventListener("pointerleave", peLeave);

    incadreaza(camera, controls, scene);
    render();

    return () => {
      pictor.current = null;
      canvas.removeEventListener("pointermove", peMove);
      canvas.removeEventListener("pointerdown", peDown);
      canvas.removeEventListener("pointerup", peUp);
      canvas.removeEventListener("pointerleave", peLeave);
      controls.removeEventListener("change", render);
      controls.dispose();
      renderer.dispose();
      [matStalp, matGrinda, matPerete, matSel, matContur].forEach((m) => m.dispose());
      materiale.forEach((m) => m.dispose());
      conturi.forEach((g) => g.dispose());
      scene.traverse((o) => {
        const m = o as THREE.Mesh;
        if (m.geometry) m.geometry.dispose();
      });
      if (renderer.domElement.parentNode === el) el.removeChild(renderer.domElement);
    };
  }, [model, height, cuPanouri]);

  // cheia de dependență e TEXTUL listei: un array nou cu același conținut ar
  // repicta la fiecare tic de polling
  const cheie = evidentiate.join("|");
  useEffect(() => {
    pictor.current?.(cheie ? cheie.split("|") : []);
  }, [cheie, model, height]);

  const t = useT();
  const tx = useTx();

  if (!model) {
    return (
      <div className="sg-muted" style={{ padding: 12, fontSize: 12 }}>
        {t("struct.loccl.cere_fem")}
      </div>
    );
  }

  // ce se întâmplă la click pe ce e sub cursor — se scrie ÎNAINTE de click,
  // ca navigarea să nu fie o surpriză. Legenda de culori a fost scoasă
  // („arată nașpa în pagină"), iar indicația stă PESTE
  // canvas, nu sub el — fără banda albă și fără să împingă pagina la hover.
  const navigabil = projectId != null;
  const subPas = sub?.pas ?? null;
  const acelasiPas = subPas != null && subPas === pasCurent;
  return (
    <div style={{ position: "relative", lineHeight: 0 }}>
      <div ref={mount} style={{ lineHeight: 0 }} />
      {navigabil && sub && (
        <div
          className="sg-muted"
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            padding: "4px 10px",
            fontSize: 12,
            lineHeight: 1.4,
            background: "rgba(246, 246, 244, 0.88)",
            pointerEvents: "none",
          }}
        >
          {subPas == null ? (
            <>
              {tx("struct.loccl.fara_pas", { ref: sub.ref })}
            </>
          ) : acelasiPas ? (
            <>
              {tx("struct.loccl.aceeasi_pagina", { ref: sub.ref })}
            </>
          ) : (
            <>
              {tx("struct.loccl.alt_pas", { pas: pasScurt(subPas), ref: sub.ref })}{" "}
              <span title={titluPas(subPas)}>({titluPas(subPas)})</span>
            </>
          )}
        </div>
      )}
    </div>
  );
}
